import React, { useCallback } from 'react'
import {
  RecordHighlight,
  HighlightsBase,
  useHighlightData,
  MemoryHighlight,
  RecordBoundingBoxHighlight,
} from '@fs/zion-image-viewer'
import { Card, Row, Button } from '@fs/zion-ui'
import { MenuClose, ContentCheck } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'

import { useSlsHighlightsContext } from '@fs/zion-sls'
import { useR2Highlights } from '../../providers/r2Provider/R2Context'
import useSlsHighlightEventListeners from '../../hooks/useSlsHighlightEventListeners'
import { useHighlightsVisibilityContext } from '../../../shared/providers/highlightsVisibilityProvider/HighlightsVisibilityProvider'
import useRecordOutlineHighlightsEnabled from '../../hooks/useRecordOutlineHighlightsEnabled'

/*
 * By using these dummy objects when highlights are disabled, we avoid potential out-of-sync issues.
 * E.g. if the user turns off highlights then triggers events that show only certain highlights, those events
 * still apply to the real highlight state — so when highlights are turned back on, they'll be correct.
 */
export const DUMMY_HIGHLIGHT_STATE = { highlights: {}, hoveredIds: [], selectedIds: [], ids: [], isShowingAll: false }
const DUMMY_HIGHLIGHT_API = {
  add: () => {},
  addMany: () => {},
  clear: () => {},
  hover: () => {},
  remove: () => {},
  select: () => {},
  showAll: () => {},
  update: () => {},
}

const SelectedContent = ({ id, freezeAttachStateList, onToggleAttachedListener }) => {
  const { attached } = useHighlightData()
  const [t] = useTranslation()
  if (freezeAttachStateList?.includes(id)) {
    return null
  }
  return (
    <Card>
      <Row wrap alignX="center">
        <Button
          emphasis="medium"
          Icon={attached ? MenuClose : ContentCheck}
          onClick={() => onToggleAttachedListener(id)}
        >
          {attached ? t('highlights-base.button.detach', 'Detach') : t('highlights-base.button.attach', 'Attach')}
        </Button>
      </Row>
    </Card>
  )
}

export default function SlsHighlightsBase({
  forSplitScreen,
  showEditHighlights,
  freezeAttachStateList,
  state,
  dispatch,
}) {
  const { highlightsVisible: showHighlights } = useHighlightsVisibilityContext()
  const {
    displayHighlights,
    splitScreenDisplayHighlights,
    recordDisplayHighlights,
    recordDisplayHighlightsApi,
    splitScreenRecordDisplayHighlights,
    splitScreenRecordDisplayHighlightsApi,
  } = useR2Highlights()
  const { isCreatingRects } = useSlsHighlightsContext()
  const recordOutlineEnabled = useRecordOutlineHighlightsEnabled()
  const { onHoverHighlight, onFocusHighlight, onUpdateHighlight, onToggleAttachedListener, onHoverRecordHighlight } =
    useSlsHighlightEventListeners({ forSplitScreen, state, dispatch })

  const SelectedContentProviderComponent = useCallback(
    ({ id }) => <SelectedContent {...{ id, freezeAttachStateList, onToggleAttachedListener }} />,
    [freezeAttachStateList, onToggleAttachedListener]
  )

  const activeRecordHighlightState = forSplitScreen ? splitScreenRecordDisplayHighlights : recordDisplayHighlights
  const activeRecordHighlightApi = forSplitScreen ? splitScreenRecordDisplayHighlightsApi : recordDisplayHighlightsApi
  const activeFieldHighlightState = forSplitScreen ? splitScreenDisplayHighlights : displayHighlights

  const recordHighlightState = showHighlights ? activeRecordHighlightState : DUMMY_HIGHLIGHT_STATE
  const recordHighlightApi = showHighlights ? activeRecordHighlightApi : DUMMY_HIGHLIGHT_API
  const fieldHighlightState = showHighlights ? activeFieldHighlightState : DUMMY_HIGHLIGHT_STATE

  // MemoryHighlight only draws its ring while hovered or selected, so records stay invisible with
  // the flag off - which is the behavior that predates the outline.
  const RecordLevelHighlight = recordOutlineEnabled ? RecordBoundingBoxHighlight : MemoryHighlight

  if (showEditHighlights) {
    return (
      <>
        <HighlightsBase
          editable
          drawMode={isCreatingRects}
          state={recordHighlightState}
          api={recordHighlightApi}
          Component={RecordLevelHighlight}
          onHover={onHoverRecordHighlight}
        />
        <HighlightsBase
          editable
          // You CANNOT use an anonymous function here, it will cause the component to re-render every time, and throw away it's clickListener funcitons at JUST the wrong time
          SelectedContent={SelectedContentProviderComponent}
          drawMode={isCreatingRects}
          state={fieldHighlightState}
          Component={RecordHighlight}
          onHover={onHoverHighlight}
          onFocus={onFocusHighlight}
          onUpdate={onUpdateHighlight}
        />
      </>
    )
  }
  return (
    <>
      <HighlightsBase
        state={recordHighlightState}
        api={recordHighlightApi}
        Component={RecordLevelHighlight}
        onHover={onHoverRecordHighlight}
      />
      <HighlightsBase
        state={fieldHighlightState}
        Component={RecordHighlight}
        onHover={onHoverHighlight}
        onFocus={onFocusHighlight}
      />
    </>
  )
}
